import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Flame, Menu as MenuIcon, X, Globe, CalendarCheck, ArrowRight } from 'lucide-react';

interface LandingHeaderProps {
  onOpenOrderApp: () => void;
}

export const LandingHeader: React.FC<LandingHeaderProps> = ({ onOpenOrderApp }) => {
  const { lang, setLang } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLabels = {
    home: { fi: 'Etusivu', en: 'Home', sv: 'Hem' },
    food: { fi: 'Ruoka & Menu', en: 'Food & Menu', sv: 'Mat & Meny' },
    bar: { fi: 'Baari & Juomat', en: 'Bar & Cocktails', sv: 'Bar & Cocktails' },
    privateBooking: { fi: 'Yksityistilaisuudet', en: 'Private Booking', sv: 'Privata Event' },
    location: { fi: 'Aukioloajat & Sijainti', en: 'Hours & Location', sv: 'Öppettider' },
    bookTable: { fi: 'Varaa Pöytä', en: 'Book Your Table', sv: 'Boka Ditt Bord' },
    bookTableShort: { fi: 'Varaa', en: 'Book', sv: 'Boka' }
  };

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const offset = 76;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = el.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <header className={`os-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="os-navbar-inner">
          {/* Logo (Clearly visible & crisp, single-line) */}
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); scrollTo('hero'); }}
            className="os-brand-logo"
            aria-label="The Oven Story Helsinki Home"
          >
            <div className="os-brand-icon">
              <Flame size={20} color="#e05a36" style={{ fill: '#e05a36' }} />
            </div>
            <div className="os-brand-text-wrap">
              <span className="os-brand-name">The Oven Story</span>
              <span className="os-brand-sub">Helsinki · Wood-Fired & Bar</span>
            </div>
          </a>

          {/* Desktop Navigation Links (Smooth Scroll) */}
          <nav aria-label="Main Navigation">
            <ul className="os-desktop-nav">
              <li>
                <button onClick={() => scrollTo('hero')} className="os-nav-link">
                  {navLabels.home[lang]}
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo('food')} className="os-nav-link">
                  {navLabels.food[lang]}
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo('bar')} className="os-nav-link">
                  {navLabels.bar[lang]}
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo('private-booking')} className="os-nav-link">
                  {navLabels.privateBooking[lang]}
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo('visit')} className="os-nav-link">
                  {navLabels.location[lang]}
                </button>
              </li>
            </ul>
          </nav>

          {/* Right Actions: Desktop Language + Book CTA + Mobile Hamburger */}
          <div className="os-nav-actions">
            {/* Desktop Language Switcher (Hidden on mobile <1024px, available in mobile drawer) */}
            <div className="os-desktop-lang">
              <Globe size={13} color="var(--os-text-muted)" style={{ marginRight: '2px' }} />
              {(['fi', 'en', 'sv'] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`os-lang-btn ${lang === l ? 'active' : ''}`}
                >
                  {l}
                </button>
              ))}
            </div>

            {/* Primary Action Button: Responsive text */}
            <button
              onClick={onOpenOrderApp}
              className="os-btn-book-table"
              title="Book your dining table or order directly from your mobile"
            >
              <CalendarCheck size={16} />
              <span className="os-btn-text-desktop">{navLabels.bookTable[lang]}</span>
              <span className="os-btn-text-mobile">{navLabels.bookTableShort[lang]}</span>
            </button>

            {/* Hamburger Toggle (Visible on Mobile & Tablet devices) */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="os-hamburger-btn"
              aria-label="Open mobile navigation menu"
              title="Valikko / Menu"
            >
              <MenuIcon size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Smart Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="os-mobile-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="os-mobile-drawer-card" onClick={(e) => e.stopPropagation()}>
            <div className="os-mobile-drawer-header">
              <div className="os-brand-logo">
                <div className="os-brand-icon">
                  <Flame size={20} color="#e05a36" style={{ fill: '#e05a36' }} />
                </div>
                <div className="os-brand-text-wrap">
                  <span className="os-brand-name" style={{ fontSize: '1.35rem' }}>The Oven Story</span>
                  <span className="os-brand-sub" style={{ display: 'block' }}>Aleksanterinkatu 18 · Helsinki</span>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="os-mobile-close-btn"
                aria-label="Close menu"
              >
                <X size={22} />
              </button>
            </div>

            {/* Smart Language Selector inside Mobile Drawer */}
            <div className="os-mobile-lang-section">
              <div className="os-mobile-lang-title">
                <Globe size={13} color="var(--os-amber)" />
                <span>{lang === 'fi' ? 'Valitse kieli' : lang === 'sv' ? 'Välj språk' : 'Select Language'}</span>
              </div>
              <div className="os-mobile-lang-tabs">
                <button
                  onClick={() => setLang('fi')}
                  className={`os-mobile-lang-tab ${lang === 'fi' ? 'active' : ''}`}
                >
                  <span className="flag-icon">🇫🇮</span> Suomi
                </button>
                <button
                  onClick={() => setLang('en')}
                  className={`os-mobile-lang-tab ${lang === 'en' ? 'active' : ''}`}
                >
                  <span className="flag-icon">🇬🇧</span> English
                </button>
                <button
                  onClick={() => setLang('sv')}
                  className={`os-mobile-lang-tab ${lang === 'sv' ? 'active' : ''}`}
                >
                  <span className="flag-icon">🇸🇪</span> Svenska
                </button>
              </div>
            </div>

            {/* Nav Items with icons */}
            <div className="os-mobile-nav-list">
              <button onClick={() => scrollTo('hero')} className="os-mobile-nav-item">
                <span className="os-mobile-nav-label">
                  <span className="os-nav-icon">✨</span>
                  {navLabels.home[lang]}
                </span>
                <ArrowRight size={18} color="var(--os-copper)" />
              </button>
              <button onClick={() => scrollTo('food')} className="os-mobile-nav-item">
                <span className="os-mobile-nav-label">
                  <span className="os-nav-icon">🍕</span>
                  {navLabels.food[lang]}
                </span>
                <ArrowRight size={18} color="var(--os-copper)" />
              </button>
              <button onClick={() => scrollTo('bar')} className="os-mobile-nav-item">
                <span className="os-mobile-nav-label">
                  <span className="os-nav-icon">🍸</span>
                  {navLabels.bar[lang]}
                </span>
                <ArrowRight size={18} color="var(--os-copper)" />
              </button>
              <button onClick={() => scrollTo('private-booking')} className="os-mobile-nav-item">
                <span className="os-mobile-nav-label">
                  <span className="os-nav-icon">🥂</span>
                  {navLabels.privateBooking[lang]}
                </span>
                <ArrowRight size={18} color="var(--os-copper)" />
              </button>
              <button onClick={() => scrollTo('visit')} className="os-mobile-nav-item">
                <span className="os-mobile-nav-label">
                  <span className="os-nav-icon">📍</span>
                  {navLabels.location[lang]}
                </span>
                <ArrowRight size={18} color="var(--os-copper)" />
              </button>
            </div>

            {/* Mobile Redirect Button */}
            <div className="os-mobile-drawer-cta">
              <button
                onClick={() => { setMobileMenuOpen(false); onOpenOrderApp(); }}
                className="os-btn-hero-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '14px' }}
              >
                <CalendarCheck size={18} />
                <span>{navLabels.bookTable[lang]}</span>
              </button>
            </div>

            {/* Mobile Drawer Footer info */}
            <div className="os-mobile-drawer-footer">
              <span>Aleksanterinkatu 18, 00170 Helsinki</span>
              <span>•</span>
              <a href="tel:+358405558920" style={{ color: 'var(--os-amber)', textDecoration: 'none' }}>
                +358 40 555 8920
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
